import { useEffect, useState } from "react";
import {
    BadgeCheck,
    CreditCard,
    Instagram,
    Twitch,
    Youtube,
    Twitter,
    User,
    Settings as SettingsIcon,
    Sparkles,
    Mail,
    Gamepad2,
    Share2,
    Briefcase,
    CalendarClock,
    Receipt,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";

import {
    SettingsTabs,
    SettingsCard,
    SectionHeader,
    InputField,
    TextareaField,
    SocialInput,
    UploadAvatarCard,
    GameSelect,
    ReferralCard,
    SaveButton,
    MINT,
    PURPLE,
} from "../Components/SettingsPrimitives";
import TheNav from "../Components/navbar";
import { auth, db } from "../Context/firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import {
    doc,
    getDoc,
    updateDoc,
    collection,
    getDocs,
} from "firebase/firestore";
import {
    getStorage,
    ref,
    uploadBytes,
    getDownloadURL,
} from "firebase/storage";
import DOMPurify from "dompurify";
import { normalizeSocialUrl } from "../utils/socialLinks";
import sanitizeFilename from "sanitize-filename";
import SEO from "../Components/SEO";
import {
    getUserEntitlements,
    getUserSubscriptionSummary,
    ONE_TIME_PRODUCTS,
} from "../config/plans";
import {
    clearCheckoutStatusParams,
    getCheckoutStatusToast,
} from "../utils/checkoutStatus";
import {
    formatPurchaseAmount,
    formatSubscriptionDate,
} from "../utils/subscriptionAccount";

const TABS = [
    { id: "profile", label: "Profile" },
    { id: "account", label: "Account" },
    { id: "portfolio", label: "Portfolio" },
];

const ENTITLEMENT_LABELS = {
    featuredPlacement: "Featured placement",
    homepageSpotlight: "Homepage spotlight",
    verifiedPortfolioBadge: "Verified portfolio badge",
    premiumThemes: "Premium themes",
    customProfileBanner: "Premium profile banner",
    advancedProfileCustomization: "Advanced customization",
    reusableEditingInstructions: "Instruction templates",
    batchProjectPlanning: "Batch planning",
    contentWorkflowBoard: "Content workflow board",
    projectOrganization: "Project organization",
    priorityCreatorProfile: "Priority creator profile",
    analyticsLite: "Analytics Lite",
    portfolioStatistics: "Portfolio Statistics",
    earlyAccess: "Early access",
    discountedCreatorReview: "Discounted creator review",
    discountedPortfolioCritique: "Discounted Portfolio Review",
    priorityProfileReview: "Priority profile review",
    higherFeaturedRotationPriority: "Higher featured rotation",
};

function formatLabel(value) {
    if (!value) return "None";
    return String(value)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getActiveEntitlementChips(entitlements = {}) {
    const chips = Object.entries(ENTITLEMENT_LABELS)
        .filter(([key]) => entitlements[key] === true)
        .map(([, label]) => label);

    if (Number(entitlements.portfolioVideoLimit) > 3) {
        chips.unshift(`${entitlements.portfolioVideoLimit} portfolio videos`);
    }

    return chips.slice(0, 8);
}

function getPurchaseProductName(productId) {
    return (
        ONE_TIME_PRODUCTS.find((product) => product.id === productId)?.name ||
        formatLabel(productId)
    );
}

function getMillis(value) {
    if (!value) return 0;
    if (typeof value.toMillis === "function") return value.toMillis();
    if (typeof value.toDate === "function") return value.toDate().getTime();
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
}

function SubscriptionSummaryCard({ userData, onManageBilling }) {
    const subscriptionSummary = getUserSubscriptionSummary(userData);
    const entitlements = getUserEntitlements(userData);
    const activeChips = getActiveEntitlementChips(entitlements);
    const planName = subscriptionSummary.plan?.name || "Free";
    const status = subscriptionSummary.status || "none";
    const subscription = subscriptionSummary.subscription || {};
    const renewalLabel = subscriptionSummary.cancelAtPeriodEnd
        ? "Access ends"
        : "Renews";
    const renewalDate = formatSubscriptionDate(subscription.currentPeriodEnd);

    return (
        <SettingsCard glow="mint">
            <SectionHeader
                icon={<CreditCard size={16} />}
                title="Subscription"
                subtitle="Plan, status, and unlocked features"
            />

            <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-white/45">
                        Current plan
                    </p>
                    <p className="mt-1 text-sm font-bold text-white">{planName}</p>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-white/45">
                        Status
                    </p>
                    <p className="mt-1 text-sm font-bold text-white">{formatLabel(status)}</p>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-white/45">
                        Billing
                    </p>
                    <p className="mt-1 text-sm font-bold text-white">
                        {formatLabel(subscriptionSummary.billingInterval)}
                    </p>
                </div>
            </div>

            <div className="mt-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex items-start gap-3">
                    <CalendarClock size={16} style={{ color: PURPLE }} />
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-white/45">
                            {renewalLabel}
                        </p>
                        <p className="mt-1 text-sm font-bold text-white">{renewalDate}</p>
                        {subscriptionSummary.cancelAtPeriodEnd && (
                            <p className="mt-1 text-xs text-white/45">
                                Your paid access remains active until the end of the current period.
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <div className="mt-5">
                <div className="mb-2 flex items-center gap-2">
                    <BadgeCheck size={14} style={{ color: MINT }} />
                    <p className="text-xs font-semibold uppercase tracking-wider text-white/45">
                        Entitlements summary
                    </p>
                </div>

                {activeChips.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                        {activeChips.map((chip) => (
                            <span
                                key={chip}
                                className="rounded-full border border-emerald-300/20 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-100"
                            >
                                {chip}
                            </span>
                        ))}
                    </div>
                ) : (
                    <p className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm text-white/55">
                        No paid entitlements yet.
                    </p>
                )}
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <Link
                    to="/pricing"
                    className="inline-flex items-center justify-center rounded-xl px-4 py-3 text-sm font-bold text-black transition active:scale-[0.98]"
                    style={{
                        background: `linear-gradient(135deg, ${MINT}, #34d399)`,
                        boxShadow: `0 12px 36px -18px ${MINT}`,
                    }}
                >
                    View pricing
                </Link>
                <button
                    type="button"
                    onClick={onManageBilling}
                    className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.05] px-4 py-3 text-sm font-bold text-white transition hover:bg-white/[0.08] active:scale-[0.98]"
                >
                    Manage billing
                </button>
            </div>
        </SettingsCard>
    );
}

function PurchaseHistoryCard({ loading, purchases }) {
    return (
        <SettingsCard>
            <SectionHeader
                icon={<Receipt size={16} />}
                title="Purchases"
                subtitle="One-time services, reviews, and visibility boosts"
            />

            {loading ? (
                <p className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm text-white/55">
                    Loading purchases...
                </p>
            ) : purchases.length === 0 ? (
                <p className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm text-white/55">
                    No one-time purchases yet.
                </p>
            ) : (
                <div className="space-y-3">
                    {purchases.map((purchase) => (
                        <div
                            key={purchase.id || purchase.purchaseId}
                            className="rounded-2xl border border-white/10 bg-white/[0.03] p-4"
                        >
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                <div>
                                    <p className="text-sm font-bold text-white">
                                        {getPurchaseProductName(purchase.productId)}
                                    </p>
                                    <p className="mt-1 text-xs text-white/45">
                                        {formatSubscriptionDate(purchase.createdAt)}
                                        {purchase.serviceRequestId
                                            ? ` · Request ${purchase.serviceRequestId}`
                                            : ""}
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-2 sm:justify-end">
                                    {purchase.amountTotal && (
                                        <span className="rounded-full border border-white/10 bg-white/[0.05] px-3 py-1 text-xs font-semibold text-white/70">
                                            {formatPurchaseAmount(
                                                purchase.amountTotal,
                                                purchase.currency
                                            )}
                                        </span>
                                    )}
                                    <span className="rounded-full border border-emerald-300/20 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-100">
                                        {formatLabel(purchase.status || "paid")}
                                    </span>
                                </div>
                            </div>

                            {purchase.expiresAt && (
                                <p className="mt-3 text-xs text-white/45">
                                    Spotlight active until {formatSubscriptionDate(purchase.expiresAt)}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </SettingsCard>
    );
}

function TikTokIcon({ size = 16 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.59a8.16 8.16 0 0 0 4.77 1.52V6.69a4.85 4.85 0 0 1-1.84 0z" />
        </svg>
    );
}

function Settings() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTab] = useState("profile");
    const [toast, setToast] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedFile, setSelectedFile] = useState(null);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [userData, setUserData] = useState(null);
    const [purchases, setPurchases] = useState([]);
    const [purchasesLoading, setPurchasesLoading] = useState(false);

    const [profile, setProfile] = useState({
        username: "",
        about: "",
        profilePicUrl: "",
    });

    const [socials, setSocials] = useState({
        twitch: "",
        instagram: "",
        youtube: "",
        tiktok: "",
        twitter: "",
    });

    const [account, setAccount] = useState({
        firstName: "",
        lastName: "",
        email: "",
        id: "",
    });

    const [portfolio, setPortfolio] = useState({
        tagline: "",
        questionOne: "",
        questionTwo: "",
        questionThree: "",
        questionFour: "",
    });

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setLoading(true);

            if (!user) {
                setPurchases([]);
                setLoading(false);
                return;
            }

            try {
                const loadedCategories = await fetchCategories();
                await fetchUserData(loadedCategories, user.uid, user.email);
                await fetchUserPurchases(user.uid);
            } catch (err) {
                console.error(err);
                showToast("Failed to load settings", "error");
            } finally {
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const checkoutToast = getCheckoutStatusToast(searchParams);
        if (!checkoutToast) return;

        setTab("account");
        showToast(checkoutToast.message, checkoutToast.type);
        setSearchParams(clearCheckoutStatusParams(searchParams), { replace: true });
    }, [searchParams, setSearchParams]);

    const showToast = (message, type = "success") => {
        setToast({ message, type });

        setTimeout(() => {
            setToast(null);
        }, 2500);
    };

    const handleManageBilling = () => {
        showToast("Billing portal is coming soon. For now, use Stripe receipts or contact support.");
    };

    const normalizeMainGame = (mainGame) => {
        if (!mainGame) return "";

        if (typeof mainGame === "string") {
            return mainGame;
        }

        if (typeof mainGame === "object") {
            return mainGame.categoryName || mainGame.name || "";
        }

        return "";
    };

    const fetchUserData = async (
        loadedCategories = categories,
        uid = auth.currentUser?.uid,
        email = auth.currentUser?.email
    ) => {
        try {
            if (!uid) return;

            const userRef = doc(db, "Users", uid);

            const docSnap = await getDoc(userRef);

            if (!docSnap.exists()) return;

            const data = docSnap.data();
            setUserData({ id: docSnap.id, ...data });

            setProfile({
                username: data.profileInfo?.username || "",
                about: data.profileInfo?.about || "",
                profilePicUrl: data.profileInfo?.profilePicUrl || "",
            });

            setSocials({
                twitch: data.socials?.twitch || "",
                instagram: data.socials?.instagram || "",
                youtube: data.socials?.youtube || "",
                tiktok: data.socials?.tiktok || "",
                twitter: data.socials?.twitter || "",
            });

            setAccount({
                firstName: data.profileInfo?.firstName || "",
                lastName: data.profileInfo?.lastName || "",
                email:
                    data.profileInfo?.email ||
                    email ||
                    "",
                id: data.profileInfo?.id || "",
            });

            setPortfolio({
                tagline: data.profileInfo?.tagline || "",

                questionOne:
                    data.profileInfo?.questions?.questionOne || "",

                questionTwo:
                    data.profileInfo?.questions?.questionTwo || "",

                questionThree:
                    data.profileInfo?.questions?.questionThree || "",

                questionFour:
                    data.profileInfo?.questions?.questionFour || "",
            });

            const savedGame = normalizeMainGame(data.profileInfo?.mainGame);
            const foundCategory = loadedCategories.find(
                (category) =>
                    category.name?.toLowerCase() === savedGame.toLowerCase()
            );

            setSelectedCategory(
                foundCategory ||
                (savedGame ? { id: savedGame, name: savedGame, image: "" } : null)
            );
        } catch (error) {
            console.error("FETCH USER ERROR:", error);
            throw error;
        }
    };

    const fetchUserPurchases = async (uid) => {
        if (!uid) return;

        setPurchasesLoading(true);
        try {
            const purchasesSnapshot = await getDocs(
                collection(db, "Users", uid, "purchases")
            );
            const purchaseRows = purchasesSnapshot.docs
                .map((purchaseDoc) => ({
                    id: purchaseDoc.id,
                    ...purchaseDoc.data(),
                }))
                .sort((a, b) => getMillis(b.createdAt) - getMillis(a.createdAt));

            setPurchases(purchaseRows);
        } catch (error) {
            console.error("FETCH PURCHASES ERROR:", error);
            showToast("Failed to load purchase history", "error");
        } finally {
            setPurchasesLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const categoriesCollection = collection(
                db,
                "Categories"
            );

            const categoriesSnapshot = await getDocs(
                categoriesCollection
            );

            const categoriesList = categoriesSnapshot.docs.map(
                (doc) => ({
                    id: doc.id,
                    name: doc.data().categoryName || "",
                    image: doc.data().categoryImage || "",
                })
            ).filter((category) => category.name);

            setCategories(categoriesList);

            return categoriesList;
        } catch (error) {
            console.error("CATEGORY ERROR:", error);
            return [];
        }
    };

    const uploadProfilePhoto = async () => {
        if (!selectedFile) return profile.profilePicUrl;

        try {
            const sanitizedFileName = sanitizeFilename(selectedFile.name);

            const sanitizedFile = new File(
                [selectedFile],
                sanitizedFileName,
                {
                    type: selectedFile.type,
                }
            );

            const storage = getStorage();

            const storageRef = ref(
                storage,
                `profilePics/${auth.currentUser.uid}`
            );

            const uploadResult = await uploadBytes(
                storageRef,
                sanitizedFile
            );

            const downloadURL = await getDownloadURL(
                uploadResult.ref
            );

            return downloadURL;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const saveProfile = async () => {
        try {
            const userRef = doc(db, "Users", auth.currentUser.uid);

            let imageUrl = profile.profilePicUrl;

            if (selectedFile) {
                imageUrl = await uploadProfilePhoto();
            }

            await updateDoc(userRef, {
                "profileInfo.username": DOMPurify.sanitize(
                    profile.username
                ),

                "profileInfo.about": DOMPurify.sanitize(
                    profile.about
                ),

                "profileInfo.profilePicUrl": imageUrl,

                "socials.twitch": DOMPurify.sanitize(
                    normalizeSocialUrl("twitch", socials.twitch)
                ),

                "socials.instagram": DOMPurify.sanitize(
                    normalizeSocialUrl("instagram", socials.instagram)
                ),

                "socials.youtube": DOMPurify.sanitize(
                    normalizeSocialUrl("youtube", socials.youtube)
                ),

                "socials.tiktok": DOMPurify.sanitize(
                    normalizeSocialUrl("tiktok", socials.tiktok)
                ),

                "socials.twitter": DOMPurify.sanitize(
                    normalizeSocialUrl("twitter", socials.twitter)
                ),
            });

            showToast("Profile saved");
        } catch (error) {
            console.error(error);
            showToast("Failed to save profile", "error");
        }
    };

    const saveAccount = async () => {
        try {
            const userRef = doc(db, "Users", auth.currentUser.uid);

            await updateDoc(userRef, {
                "profileInfo.firstName": DOMPurify.sanitize(
                    account.firstName
                ),

                "profileInfo.lastName": DOMPurify.sanitize(
                    account.lastName
                ),

                "profileInfo.mainGame": DOMPurify.sanitize(
                    selectedCategory?.name || ""
                ),
            });

            showToast("Account saved");
        } catch (error) {
            console.error(error);
            showToast("Failed to save account", "error");
        }
    };

    const savePortfolio = async () => {
        try {
            const userRef = doc(db, "Users", auth.currentUser.uid);

            await updateDoc(userRef, {
                "profileInfo.tagline": DOMPurify.sanitize(
                    portfolio.tagline
                ),

                "profileInfo.questions.questionOne":
                    DOMPurify.sanitize(portfolio.questionOne),

                "profileInfo.questions.questionTwo":
                    DOMPurify.sanitize(portfolio.questionTwo),

                "profileInfo.questions.questionThree":
                    DOMPurify.sanitize(portfolio.questionThree),

                "profileInfo.questions.questionFour":
                    DOMPurify.sanitize(portfolio.questionFour),
            });

            showToast("Portfolio saved");
        } catch (error) {
            console.error(error);
            showToast("Failed to save portfolio", "error");
        }
    };

    const copyReferral = async () => {
        try {
            await navigator.clipboard.writeText(
                `https://kre8content.com/signup?referral=${account.id}`
            );

            showToast("Referral link copied");
        } catch (error) {
            showToast("Failed to copy referral link", "error");
        }
    };

    return (
        <>
            <SEO
                title="Settings | Kre8"
                description="Private Kre8 account and profile settings."
                canonicalPath="/settings"
                noindex
            />
            <TheNav />
            
            <div className="relative min-h-screen w-full overflow-x-hidden bg-[#07060d] text-white">
                <div className="mx-auto w-full max-w-2xl px-4 pb-28 pt-8 sm:pt-12">

                    <div className="mb-6">
                        <span
                            className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider backdrop-blur"
                            style={{ color: MINT }}
                        >
                            <SettingsIcon size={12} />
                            Settings
                        </span>

                        <h1 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
                            Make your profile{" "}
                            <span
                                className="bg-clip-text text-transparent"
                                style={{
                                    backgroundImage: `linear-gradient(135deg, ${MINT}, ${PURPLE})`,
                                }}
                            >
                                unmissable
                            </span>
                        </h1>
                    </div>

                    <SettingsTabs
                        tabs={TABS}
                        value={tab}
                        onChange={setTab}
                    />

                    {toast && (
                        <div
                            className={`mt-5 rounded-xl px-4 py-3 text-sm font-medium ${toast.type === "error"
                                ? "bg-red-500/20 text-red-300"
                                : "bg-emerald-500/20 text-emerald-300"
                                }`}
                        >
                            {toast.message}
                        </div>
                    )}

                    <div className="mt-6 space-y-5">

                        {/* PROFILE */}

                        {tab === "profile" && (
                            <>
                                <UploadAvatarCard
                                    photo={
                                        selectedFile
                                            ? URL.createObjectURL(selectedFile)
                                            : profile.profilePicUrl
                                    }
                                    onPick={(file) => {
                                        if (!file) {
                                            setSelectedFile(null);

                                            return;
                                        }

                                        setSelectedFile(file);
                                    }}
                                />

                                <SettingsCard>

                                    <SectionHeader
                                        icon={<User size={16} />}
                                        title="Your identity"
                                        subtitle="Username and intro"
                                    />

                                    <div className="space-y-4">

                                        <InputField
                                            label="Username"
                                            value={profile.username}
                                            onChange={(v) =>
                                                setProfile((prev) => ({
                                                    ...prev,
                                                    username: v,
                                                }))
                                            }
                                            placeholder="yourname"
                                        />

                                        <TextareaField
                                            label="About"
                                            value={profile.about}
                                            onChange={(v) =>
                                                setProfile((prev) => ({
                                                    ...prev,
                                                    about: v,
                                                }))
                                            }
                                            placeholder="Tell creators about yourself..."
                                            rows={5}
                                        />

                                    </div>
                                </SettingsCard>

                                <SettingsCard glow="mint">

                                    <SectionHeader
                                        icon={<Share2 size={16} />}
                                        title="Socials"
                                    />

                                    <div className="space-y-3">

                                        <SocialInput
                                            icon={<Twitch size={16} />}
                                            platform="Twitch"
                                            value={socials.twitch}
                                            onChange={(v) =>
                                                setSocials((prev) => ({
                                                    ...prev,
                                                    twitch: v,
                                                }))
                                            }
                                            placeholder="twitch.tv/username"
                                        />

                                        <SocialInput
                                            icon={<Instagram size={16} />}
                                            platform="Instagram"
                                            value={socials.instagram}
                                            onChange={(v) =>
                                                setSocials((prev) => ({
                                                    ...prev,
                                                    instagram: v,
                                                }))
                                            }
                                            placeholder="@instagram"
                                        />

                                        <SocialInput
                                            icon={<Youtube size={16} />}
                                            platform="YouTube"
                                            value={socials.youtube}
                                            onChange={(v) =>
                                                setSocials((prev) => ({
                                                    ...prev,
                                                    youtube: v,
                                                }))
                                            }
                                            placeholder="youtube.com/@channel"
                                        />

                                        <SocialInput
                                            icon={<TikTokIcon />}
                                            platform="TikTok"
                                            value={socials.tiktok}
                                            onChange={(v) =>
                                                setSocials((prev) => ({
                                                    ...prev,
                                                    tiktok: v,
                                                }))
                                            }
                                            placeholder="@tiktok"
                                        />

                                        <SocialInput
                                            icon={<Twitter size={16} />}
                                            platform="Twitter / X"
                                            value={socials.twitter}
                                            onChange={(v) =>
                                                setSocials((prev) => ({
                                                    ...prev,
                                                    twitter: v,
                                                }))
                                            }
                                            placeholder="@twitter"
                                        />

                                    </div>
                                </SettingsCard>

                                <SaveButton onClick={saveProfile} />

                            </>
                        )}

                        {/* ACCOUNT */}

                        {tab === "account" && (
                            <>
                                <SubscriptionSummaryCard
                                    userData={userData}
                                    onManageBilling={handleManageBilling}
                                />

                                <PurchaseHistoryCard
                                    loading={purchasesLoading}
                                    purchases={purchases}
                                />

                                <SettingsCard>

                                    <SectionHeader
                                        icon={<Mail size={16} />}
                                        title="Account info"
                                    />

                                    <div className="space-y-4">

                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

                                            <InputField
                                                label="First Name"
                                                value={account.firstName}
                                                onChange={(v) =>
                                                    setAccount((prev) => ({
                                                        ...prev,
                                                        firstName: v,
                                                    }))
                                                }
                                            />

                                            <InputField
                                                label="Last Name"
                                                value={account.lastName}
                                                onChange={(v) =>
                                                    setAccount((prev) => ({
                                                        ...prev,
                                                        lastName: v,
                                                    }))
                                                }
                                            />

                                        </div>

                                        <InputField
                                            label="Email Address"
                                            value={account.email}
                                            readOnly
                                        />

                                    </div>
                                </SettingsCard>

                                <SettingsCard glow="mint">

                                    <SectionHeader
                                        icon={<Gamepad2 size={16} />}
                                        title="Main Game"
                                    />

                                    <GameSelect
                                        value={selectedCategory?.name ?? ""}
                                        onChange={(gameName) => {
                                            const found = categories.find(
                                                (g) => g.name === gameName
                                            );

                                            setSelectedCategory(
                                                found || { id: gameName, name: gameName, image: "" }
                                            );
                                        }}
                                        options={categories.map((c) => c.name)}
                                    />

                                </SettingsCard>

                                <div onClick={copyReferral}>
                                    <ReferralCard
                                        code={`https://kre8content.com/signup?referral=${account.id}`}
                                    />
                                </div>

                                <SaveButton onClick={saveAccount} />
                            </>
                        )}

                        {/* PORTFOLIO */}

                        {tab === "portfolio" && (
                            <>
                                <SettingsCard>

                                    <SectionHeader
                                        icon={<Sparkles size={16} />}
                                        title="What is your tagline?"
                                    />

                                    <InputField
                                        value={portfolio.tagline}
                                        onChange={(v) =>
                                            setPortfolio((prev) => ({
                                                ...prev,
                                                tagline: v,
                                            }))
                                        }
                                        placeholder="Your tagline"
                                    />

                                </SettingsCard>

                                <SettingsCard glow="mint">

                                    <SectionHeader
                                        icon={<Briefcase size={16} />}
                                        title="What things would make the editing gig the easiest for you?"
                                    />

                                    <TextareaField
                                        value={portfolio.questionOne}
                                        onChange={(v) =>
                                            setPortfolio((prev) => ({
                                                ...prev,
                                                questionOne: v,
                                            }))
                                        }
                                        rows={5}
                                    />

                                </SettingsCard>

                                <SettingsCard>

                                    <SectionHeader
                                        icon={<Mail size={16} />}
                                        title="How can you make the editing gig easiest for the other person?"
                                    />

                                    <TextareaField
                                        value={portfolio.questionTwo}
                                        onChange={(v) =>
                                            setPortfolio((prev) => ({
                                                ...prev,
                                                questionTwo: v,
                                            }))
                                        }
                                        rows={5}
                                    />

                                </SettingsCard>

                                <SettingsCard glow="mint">

                                    <SectionHeader
                                        icon={<Sparkles size={16} />}
                                        title="What are the pain points others may have while working with you?"
                                    />

                                    <TextareaField
                                        value={portfolio.questionThree}
                                        onChange={(v) =>
                                            setPortfolio((prev) => ({
                                                ...prev,
                                                questionThree: v,
                                            }))
                                        }
                                        rows={5}
                                    />

                                </SettingsCard>

                                <SettingsCard>

                                    <SectionHeader
                                        icon={<Briefcase size={16} />}
                                        title="What do you bring to the table?"
                                    />

                                    <TextareaField
                                        value={portfolio.questionFour}
                                        onChange={(v) =>
                                            setPortfolio((prev) => ({
                                                ...prev,
                                                questionFour: v,
                                            }))
                                        }
                                        rows={5}
                                    />

                                </SettingsCard>

                                <SaveButton onClick={savePortfolio} />

                            </>
                        )}

                    </div>
                </div>
            </div>
        </>
    );
}

export default Settings;
